import { useEffect } from "react";
import { motion } from "framer-motion";
import { Users, ShieldCheck, HeartHandshake, Leaf } from "lucide-react";
import SEO from "@/components/SEO";
import PathwayCard from "@/components/PathwayCard";
import { PATHWAYS, PATHWAY_ORDER } from "@/data/pathways";
import { track } from "@/lib/track";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const TRUST = [
  { icon: Users, text: "Personalized Guidance" },
  { icon: ShieldCheck, text: "Education First" },
  { icon: HeartHandshake, text: "No Pressure Conversations" },
  { icon: Leaf, text: "A Brighter Tomorrow" },
];

export default function PathwayRouter() {
  useEffect(() => {
    track("homepage_viewed");
  }, []);

  return (
    <div
      className="text-[#F6F1E7] relative overflow-hidden bg-[#0B1526]"
      data-testid="pathway-router"
    >
      <SEO path="/" />
      <section className="relative bg-[#0B1526]" data-testid="router-hero">
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 md:pt-24 pb-16 sm:pb-20 md:pb-24 text-center">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.12 } },
            }}
          >
            <motion.h1
              variants={fadeUp}
              className="font-serif text-[31px] min-[400px]:text-[35px] sm:text-[54px] lg:text-[68px] font-medium leading-[1.06] tracking-tight text-[#F6F1E7]"
              data-testid="router-headline"
            >
              What would you like help
              <br />
              understanding{" "}
              <span className="lw-script champagne-text text-[1.32em] leading-none align-baseline">
                today?
              </span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-6 sm:mt-8 text-[15px] sm:text-lg lg:text-xl text-[#F6F1E7]/90 leading-relaxed max-w-2xl mx-auto"
            >
              Take a quick, personalized checkup to get clarity,
              <br className="hidden sm:block" /> ask the right questions, and
              take the next step with confidence.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <div className="router-bg relative">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pb-14 md:pb-20">
          <motion.section
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: {
                transition: { staggerChildren: 0.12, delayChildren: 0.25 },
              },
            }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-7"
            data-testid="pathway-grid"
          >
            {PATHWAY_ORDER.map((key, i) => (
              <PathwayCard key={key} pathway={PATHWAYS[key]} index={i} />
            ))}
          </motion.section>

          <section
            className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4"
            data-testid="trust-strip"
          >
            {TRUST.map(({ icon: Icon, text }, i) => (
              <div
                key={text}
                className={`flex items-center gap-3 ${i > 0 ? "md:border-l md:border-[#D6BB7C]/25 md:pl-6" : ""}`}
              >
                <span className="w-10 h-10 shrink-0 rounded-full border border-[#D6BB7C]/60 flex items-center justify-center">
                  <Icon
                    className="w-[18px] h-[18px] text-[#D6BB7C]"
                    strokeWidth={2}
                  />
                </span>
                <span className="text-sm font-semibold text-[#F6F1E7]/90 leading-snug">
                  {text}
                </span>
              </div>
            ))}
          </section>

          <section className="mt-14 md:mt-20 text-center max-w-2xl mx-auto">
            <p className="font-serif italic text-2xl sm:text-3xl text-[#F6F1E7] leading-snug">
              “Planning today creates the freedom your tomorrow deserves.”
            </p>
            <div className="hairline-gold w-20 mx-auto mt-6" />
            <p
              className="mt-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#D6BB7C]"
              data-testid="router-brand-line"
            >
              Education first. Understand where you are. Decide what makes sense
              next.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
